/**
 * UI App Coordinator
 *
 * Main entry point that coordinates all controllers and services
 * for the verification UI. Handles screen navigation, initialization,
 * and wiring between components.
 */

import {
  verificationState,
  VerificationState,
  ScreenName,
  LivenessAction,
  DocumentType,
} from './state/verification-state';
import { LightingController } from './controllers/lighting-controller';
import { FaceDetectionController } from './controllers/face-detection-controller';
import { LivenessController } from './controllers/liveness-controller';
import { DocumentController } from './controllers/document-controller';
import { BackendSyncService, createBackendSyncService } from './services/backend-sync-service';
import { showElement, hideElement, setText, getElement } from './utils/dom-utils';
import { VerificationController } from '../verification';

// Re-export ScreenName as ScreenId for backward compatibility
export type ScreenId = ScreenName;

/**
 * Configuration for the UI App.
 */
export interface UIAppConfig {
  /** API base URL */
  apiUrl: string;
  /** API key from URL params */
  apiKey: string;
  /** Callback URL for final redirect */
  callbackUrl: string | null;
  /** Session ID from URL params */
  sessionId: string | null;
  /** Verification mode (demo or live) */
  mode: string | null;
  /** Debug mode flag */
  debug?: boolean;
}

/**
 * Events emitted by the UI App.
 */
export interface UIAppEvents {
  onScreenChange?: (screenId: ScreenId, previousScreen: ScreenId | null) => void;
  onVerificationComplete?: (success: boolean, result?: unknown) => void;
  onError?: (error: Error) => void;
}

/**
 * UIApp is the main coordinator for the verification UI.
 *
 * Responsibilities:
 * - Initializes all controllers and services
 * - Manages screen navigation
 * - Handles verification flow logic
 * - Coordinates between SDK and UI
 */
export class UIApp {
  private config: UIAppConfig;
  private events: UIAppEvents;

  // Controllers
  private lighting: LightingController;
  private faceDetection: FaceDetectionController;
  private liveness: LivenessController;
  private document: DocumentController;

  // Services
  private backend: BackendSyncService;

  // State
  private currentScreen: ScreenId = 'screen-welcome';
  private sdkController: VerificationController | null = null;
  private fetchedMethods: string[] = [];

  constructor(config: UIAppConfig, events: UIAppEvents = {}) {
    this.config = config;
    this.events = events;

    // Initialize controllers
    this.lighting = new LightingController();
    this.faceDetection = new FaceDetectionController();
    this.liveness = new LivenessController();
    this.document = new DocumentController();

    // Initialize backend service
    this.backend = createBackendSyncService(config.apiUrl, config.apiKey, config.sessionId);

    // Store callback URL in state
    verificationState.set('callbackUrl', config.callbackUrl);

    // Wire up controller dependencies
    this.wireControllers();
  }

  /**
   * Wires up controller dependencies and callbacks.
   */
  private wireControllers(): void {
    // Lighting affects face detection and liveness
    const isLightingAcceptable = () => this.lighting.isAcceptable();

    // Screen checks
    const isFaceScreen = () => this.currentScreen === 'screen-face-detection';
    const isLivenessScreen = () => this.currentScreen === 'screen-liveness-blink';

    // Face detection
    this.faceDetection.setLightingCheck(isLightingAcceptable);
    this.faceDetection.setActiveScreenCheck(isFaceScreen);

    // Liveness
    this.liveness.setLightingCheck(isLightingAcceptable);
    this.liveness.setActiveScreenCheck(isLivenessScreen);
  }

  /**
   * Sets the SDK controller reference.
   */
  setSdkController(controller: VerificationController): void {
    this.sdkController = controller;
    this.liveness.setSdkController(controller);
  }

  /**
   * Sets the fetched verification methods.
   */
  setMethods(methods: string[]): void {
    this.fetchedMethods = methods;

    // Update liveness actions if needed
    const livenessActions = methods.includes('liveness')
      ? (['BLINK', 'SMILE'] as LivenessAction[])
      : [];
    if (livenessActions.length > 0) {
      this.liveness.setActions(livenessActions);
    }

    // Update state
    verificationState.update('requirements', { methods });
  }

  /**
   * Checks if a verification method is required.
   */
  isMethodRequired(method: string): boolean {
    return this.fetchedMethods.includes(method);
  }

  /**
   * Navigates to a screen.
   */
  goToScreen(screenId: ScreenId): void {
    const previousScreen = this.currentScreen;

    // Run cleanup for previous screen
    this.onScreenLeave(previousScreen);

    // Update state
    this.currentScreen = screenId;
    verificationState.update('screen', {
      current: screenId,
      history: [...verificationState.get('screen').history, previousScreen],
    });

    // Hide all screens, show target
    document.querySelectorAll('.screen').forEach((el) => {
      el.classList.add('hidden');
    });
    showElement(screenId);

    // Run initialization for new screen
    this.onScreenEnter(screenId);

    // Emit event
    this.events.onScreenChange?.(screenId, previousScreen);

    console.log('[UIApp] Screen:', previousScreen, '->', screenId);
  }

  /**
   * Called when leaving a screen.
   */
  private onScreenLeave(screenId: ScreenId): void {
    switch (screenId) {
      case 'screen-face-detection':
        this.faceDetection.cancelCountdown();
        this.lighting.stop();
        break;

      case 'screen-liveness-blink':
        this.lighting.stop();
        break;

      case 'screen-document-camera':
        this.document.stopCamera();
        this.lighting.stop();
        break;

      case 'screen-age':
        this.lighting.stop();
        break;
    }
  }

  /**
   * Called when entering a screen.
   */
  private onScreenEnter(screenId: ScreenId): void {
    switch (screenId) {
      case 'screen-face-detection':
        this.initFaceDetectionScreen();
        break;

      case 'screen-liveness-blink':
        this.initLivenessScreen();
        break;

      case 'screen-age':
        this.initAgeScreen();
        break;

      case 'screen-document-type':
        // Reset document state
        this.document.reset();
        break;

      case 'screen-document-camera':
        this.initDocumentCameraScreen();
        break;

      case 'screen-success':
        this.updateSummary();
        break;
    }
  }

  /**
   * Initializes the face detection screen.
   */
  private initFaceDetectionScreen(): void {
    const videoElement = getElement<HTMLVideoElement>('camera-feed');
    if (videoElement) {
      // Start lighting monitoring
      this.lighting.start(videoElement);
      this.lighting.onStatusChange((result) => {
        if (result.status !== 'good') {
          this.faceDetection.cancelCountdown();
        }
      });
    }

    // Set up face detection events
    this.faceDetection = new FaceDetectionController(
      {},
      {
        onCapture: (_selfie) => {
          console.log('[UIApp] First selfie captured');
          this.goToScreen('screen-liveness-blink');
        },
        onStatusChange: (message, instruction) => {
          setText('face-status-text', message);
          setText('face-instruction', instruction);
        },
      }
    );
    this.faceDetection.setLightingCheck(() => this.lighting.isAcceptable());
    this.faceDetection.setActiveScreenCheck(() => this.currentScreen === 'screen-face-detection');
  }

  /**
   * Initializes the liveness screen.
   */
  private initLivenessScreen(): void {
    const videoElement = getElement<HTMLVideoElement>('camera-feed');
    if (videoElement) {
      this.lighting.start(videoElement);
    }

    // Start liveness flow
    this.liveness.start().then((success) => {
      if (success) {
        this.goToScreen('screen-age');
      }
    });
  }

  /**
   * Initializes the age estimation screen.
   */
  private initAgeScreen(): void {
    const videoElement = getElement<HTMLVideoElement>('camera-feed');
    if (videoElement) {
      this.lighting.start(videoElement);
    }
  }

  /**
   * Initializes the document camera screen.
   */
  private initDocumentCameraScreen(): void {
    const videoElement = getElement<HTMLVideoElement>('camera-document');
    const containerElement = getElement('camera-container-document');

    if (videoElement) {
      this.document.setVideoElement(videoElement);
    }
    if (containerElement) {
      this.document.setContainerElement(containerElement);
    }

    this.document.startCamera();

    // Start lighting monitoring
    if (videoElement) {
      this.lighting.start(videoElement);
    }
  }

  /**
   * Updates the success screen summary.
   */
  private updateSummary(): void {
    // Implementation depends on what data is available
    console.log('[UIApp] Updating success summary');
  }

  // ========================================================================
  // Flow Handlers
  // ========================================================================

  /**
   * Handles starting verification from welcome screen.
   */
  handleStartVerification(): void {
    console.log('[UIApp] Starting verification...');
    this.goToScreen('screen-preparing');
  }

  /**
   * Handles permissions continue (camera granted).
   */
  handlePermissionsContinue(): void {
    console.log('[UIApp] Permissions granted, going to face detection');
    this.goToScreen('screen-face-detection');
  }

  /**
   * Handles liveness retry.
   */
  handleLivenessRetry(): void {
    this.liveness.retry();
  }

  /**
   * Handles liveness start over.
   */
  handleLivenessStartOver(): void {
    this.liveness.startOver();
    this.goToScreen('screen-welcome');
  }

  /**
   * Handles document type selection.
   */
  handleDocumentTypeSelect(type: DocumentType): void {
    this.document.setDocumentType(type);
    this.goToScreen('screen-document-camera');
  }

  /**
   * Handles document capture button.
   */
  handleDocumentCapture(): void {
    const dataUrl = this.document.capture();
    if (dataUrl) {
      this.goToScreen('screen-document-preview');
    }
  }

  /**
   * Handles document preview confirm.
   */
  handleDocumentConfirm(): void {
    this.document.confirmCapture();

    if (this.document.isComplete()) {
      this.goToScreen('screen-processing');
    } else {
      // Need back side
      this.document.startCamera();
      this.goToScreen('screen-document-camera');
    }
  }

  /**
   * Handles document preview retake.
   */
  handleDocumentRetake(): void {
    this.document.retake();
    this.document.startCamera();
    this.goToScreen('screen-document-camera');
  }

  /**
   * Handles verification completion.
   */
  handleVerificationComplete(success: boolean, result?: unknown): void {
    verificationState.set('result', { success, ...(result as object) });

    if (success) {
      this.goToScreen('screen-success');
    } else {
      this.goToScreen('screen-failure');
    }

    this.events.onVerificationComplete?.(success, result);
  }

  // ========================================================================
  // Status Display
  // ========================================================================

  /**
   * Shows a status message.
   */
  showStatus(message: string, type: 'info' | 'success' | 'error' = 'info'): void {
    const statusEl = getElement('status-bar');
    if (!statusEl) return;

    setText('status-bar', message);
    statusEl.className = `status-bar status-${type}`;
    showElement('status-bar');

    // Error messages stay visible longer (8s) so users can read them.
    // Other messages auto-hide after 3 seconds.
    const hideDelay = type === 'error' ? 8000 : 3000;
    setTimeout(() => hideElement('status-bar'), hideDelay);
  }

  // ========================================================================
  // Getters
  // ========================================================================

  /**
   * Gets the current screen.
   */
  getCurrentScreen(): ScreenId {
    return this.currentScreen;
  }

  /**
   * Gets the lighting controller.
   */
  getLightingController(): LightingController {
    return this.lighting;
  }

  /**
   * Gets the face detection controller.
   */
  getFaceDetectionController(): FaceDetectionController {
    return this.faceDetection;
  }

  /**
   * Gets the liveness controller.
   */
  getLivenessController(): LivenessController {
    return this.liveness;
  }

  /**
   * Gets the document controller.
   */
  getDocumentController(): DocumentController {
    return this.document;
  }

  /**
   * Gets the backend service.
   */
  getBackendService(): BackendSyncService {
    return this.backend;
  }

  /**
   * Gets the full verification state.
   */
  getState(): VerificationState {
    return verificationState.dump();
  }

  // ========================================================================
  // Cleanup
  // ========================================================================

  /**
   * Resets the app to initial state.
   */
  reset(): void {
    this.lighting.dispose();
    this.faceDetection.reset();
    this.liveness.reset();
    this.document.reset();
    this.backend.reset();
    verificationState.reset();
    this.currentScreen = 'screen-welcome';
  }

  /**
   * Disposes of the app.
   */
  dispose(): void {
    this.lighting.dispose();
    this.faceDetection.dispose();
    this.liveness.dispose();
    this.document.dispose();
  }

  /**
   * Get the SDK controller (for debugging)
   */
  getController(): VerificationController | null {
    return this.sdkController;
  }

  /**
   * Get the underlying SDK instance (for debugging)
   */
  getSDK(): ReturnType<VerificationController['getSDK']> | null {
    return this.sdkController?.getSDK() ?? null;
  }
}

/**
 * Creates and initializes a UIApp from URL parameters.
 */
export function initUIApp(config: UIAppConfig, events?: UIAppEvents): UIApp {
  const app = new UIApp(config, events);

  // Make available for debugging
  (window as any).__uiApp = app;
  (window as any).__appbouncer_controller = app.getController();
  (window as any).__appbouncer_sdk = app.getSDK();

  return app;
}

/**
 * Parses URL parameters for UIApp configuration.
 */
export function parseUrlParams(): Partial<UIAppConfig> {
  const params = new URLSearchParams(window.location.search);

  return {
    apiKey: params.get('api_key') ?? '',
    callbackUrl: params.get('callback_url'),
    sessionId: params.get('session_id'),
    mode: params.get('mode'),
  };
}
